/**
 * One video player with a small imperative API, whatever the source:
 *   YouTube (IFrame API), Vimeo (postMessage API), HTML5 <video> (uploaded / direct files),
 *   anything else embeddable (Cloudflare Stream…) falls back to a plain iframe without time control.
 *
 *   const ref = useRef();
 *   <VideoPlayer ref={ref} url={node.external_url} src={streamUrl} title="…" />
 *   await ref.current.getCurrentTime()   // seconds (0 when the source cannot report it)
 *   ref.current.pause() / play() / seekTo(seconds)
 *   ref.current.canSeek                  // false for plain-iframe sources
 */
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

export function parseVideoUrl(url) {
  if (!url) return { kind: 'none' };
  const yt = url.match(/(?:youtube\.com\/watch\?(?:.*&)?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{6,})/);
  if (yt) return { kind: 'youtube', id: yt[1] };
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: 'vimeo', id: vm[1], embed: `https://player.vimeo.com/video/${vm[1]}?api=1` };
  if (url.includes('cloudflarestream.com') || url.includes('videodelivery.net')) {
    if (url.includes('/iframe/') || url.includes('iframe.cloudflarestream.com')) return { kind: 'iframe', embed: url };
    const m = url.match(/(?:cloudflarestream\.com|videodelivery\.net)\/([a-zA-Z0-9]+)/);
    if (m) return { kind: 'iframe', embed: `https://iframe.cloudflarestream.com/${m[1]}` };
  }
  return { kind: 'file' };
}

export const formatTime = (s) => {
  const t = Math.max(0, Math.floor(Number(s) || 0));
  const h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = t % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
};

let ytApiPromise = null;
function loadYouTubeApi() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (!ytApiPromise) {
    ytApiPromise = new Promise((resolve) => {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => { prev?.(); resolve(window.YT); };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const s = document.createElement('script');
        s.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(s);
      }
    });
  }
  return ytApiPromise;
}

const VideoPlayer = forwardRef(function VideoPlayer({ url, src, title, className = 'w-full h-full', onReady, onTimeHint }, ref) {
  const info = parseVideoUrl(url);
  const kind = info.kind === 'file' || info.kind === 'none' ? 'file' : info.kind;
  const hostRef = useRef(null);
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const ytRef = useRef(null);
  const vimeoTime = useRef(0);
  const [ready, setReady] = useState(kind === 'file');

  // YouTube
  useEffect(() => {
    if (kind !== 'youtube' || !hostRef.current) return undefined;
    let disposed = false;
    const host = hostRef.current;
    const mount = document.createElement('div');
    host.appendChild(mount);
    loadYouTubeApi().then((YT) => {
      if (disposed) return;
      ytRef.current = new YT.Player(mount, {
        videoId: info.id, width: '100%', height: '100%',
        playerVars: { rel: 0, modestbranding: 1, enablejsapi: 1, origin: window.location.origin },
        events: { onReady: () => { if (!disposed) { setReady(true); onReady?.(); } } },
      });
    });
    return () => {
      disposed = true;
      try { ytRef.current?.destroy(); } catch { /* ignore */ }
      ytRef.current = null;
      if (host.contains(mount)) host.removeChild(mount);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, info.id]);

  // Vimeo: keep the last reported time
  useEffect(() => {
    if (kind !== 'vimeo') return undefined;
    const onMessage = (e) => {
      if (!/vimeo\.com$/.test(new URL(e.origin).hostname)) return;
      let data = e.data;
      try { if (typeof data === 'string') data = JSON.parse(data); } catch { return; }
      if (data?.event === 'ready') {
        setReady(true); onReady?.();
        iframeRef.current?.contentWindow?.postMessage(JSON.stringify({ method: 'addEventListener', value: 'timeupdate' }), '*');
      }
      if (data?.event === 'timeupdate' && data.data) vimeoTime.current = data.data.seconds;
      if (data?.method === 'getCurrentTime') vimeoTime.current = data.value;
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [kind, onReady]);

  const vimeoPost = (method, value) => iframeRef.current?.contentWindow?.postMessage(JSON.stringify(value === undefined ? { method } : { method, value }), '*');

  useImperativeHandle(ref, () => ({
    kind,
    canSeek: kind === 'youtube' || kind === 'vimeo' || kind === 'file',
    ready,
    async getCurrentTime() {
      if (kind === 'file') return videoRef.current?.currentTime || 0;
      if (kind === 'youtube') { try { return ytRef.current?.getCurrentTime?.() || 0; } catch { return 0; } }
      if (kind === 'vimeo') { vimeoPost('getCurrentTime'); await new Promise((r) => setTimeout(r, 80)); return vimeoTime.current || 0; }
      return 0;
    },
    pause() {
      if (kind === 'file') videoRef.current?.pause();
      else if (kind === 'youtube') { try { ytRef.current?.pauseVideo?.(); } catch { /* ignore */ } }
      else if (kind === 'vimeo') vimeoPost('pause');
    },
    play() {
      if (kind === 'file') videoRef.current?.play?.().catch?.(() => {});
      else if (kind === 'youtube') { try { ytRef.current?.playVideo?.(); } catch { /* ignore */ } }
      else if (kind === 'vimeo') vimeoPost('play');
    },
    seekTo(seconds) {
      const t = Math.max(0, Number(seconds) || 0);
      if (kind === 'file' && videoRef.current) { videoRef.current.currentTime = t; }
      else if (kind === 'youtube') { try { ytRef.current?.seekTo?.(t, true); } catch { /* ignore */ } }
      else if (kind === 'vimeo') vimeoPost('setCurrentTime', t);
    },
  }), [kind, ready]);

  if (kind === 'youtube') return <div ref={hostRef} className={`${className} [&>div]:w-full [&>div]:h-full [&_iframe]:w-full [&_iframe]:h-full`} data-testid="video-player-youtube" />;
  if (kind === 'vimeo' || kind === 'iframe') {
    return <iframe ref={iframeRef} src={info.embed} className={className} allowFullScreen title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" data-testid={`video-player-${kind}`} />;
  }
  return <video ref={videoRef} src={src} className={className} controls controlsList="nodownload" title={title} data-testid="video-player" onTimeUpdate={onTimeHint ? (e) => onTimeHint(e.currentTarget.currentTime) : undefined} />;
});

export default VideoPlayer;
